import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { safeValidateAddress } from '@mls-wallet/shared-types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useEditAddressBook } from '@/hooks/organization/use-edit-address-book';
import { getRequestErrorMessage } from '@/lib/api/get-request-error-message';
import { cn } from '@/lib/utils';

interface NewAddressBookEntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
  /** A known address to label, kept read-only so the action cannot change its target. */
  addressToLabel?: string;
}

export function NewAddressBookEntryDialog({
  open,
  onOpenChange,
  onCreated,
  addressToLabel,
}: NewAddressBookEntryDialogProps) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { editAddressBook, isLoading } = useEditAddressBook();

  useEffect(() => {
    if (!open) {
      setName('');
      setAddress('');
      setErrorMessage(null);
    } else if (addressToLabel !== undefined) {
      setAddress(addressToLabel);
      setErrorMessage(null);
    }
  }, [open, addressToLabel]);

  const trimmedName = name.trim();
  const trimmedAddress = address.trim();
  const addressValidation = safeValidateAddress(trimmedAddress);
  const showInvalidAddress =
    trimmedAddress.length > 0 && !addressValidation.isValid;
  const isSubmitDisabled =
    trimmedName.length === 0 ||
    trimmedAddress.length === 0 ||
    !addressValidation.isValid ||
    isLoading;

  const handleSubmit = async () => {
    if (isSubmitDisabled) {
      return;
    }

    setErrorMessage(null);

    try {
      await editAddressBook({
        instructions: [
          {
            action: 'upsert',
            address: addressValidation.checksumAddress ?? trimmedAddress,
            name: trimmedName,
          },
        ],
      });
      onCreated();
      onOpenChange(false);
    } catch (error) {
      setErrorMessage(
        getRequestErrorMessage(error, 'Failed to create address book entry.')
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New address label</DialogTitle>
          <DialogDescription>
            Save a label for an EVM address in your organization&apos;s address
            book.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="address-book-entry-name">Name</Label>
            <Input
              id="address-book-entry-name"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setErrorMessage(null);
              }}
              placeholder="Treasury Safe"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address-book-entry-address">Address</Label>
            <Input
              id="address-book-entry-address"
              value={address}
              readOnly={addressToLabel !== undefined}
              onChange={(event) => {
                setAddress(event.target.value);
                setErrorMessage(null);
              }}
              placeholder="0x..."
              spellCheck={false}
              aria-invalid={showInvalidAddress}
              className={cn(
                'font-mono',
                showInvalidAddress &&
                  'border-red-500 focus-visible:ring-red-500'
              )}
            />
            {addressValidation.checksumAddress &&
              addressValidation.checksumAddress !== trimmedAddress && (
                <p className="text-xs text-muted-foreground px-1">
                  Checksummed:{' '}
                  <span className="font-mono">
                    {addressValidation.checksumAddress}
                  </span>
                </p>
              )}
            {showInvalidAddress && (
              <p className="text-sm text-red-600 px-1">Enter a valid address</p>
            )}
          </div>
        </div>

        {errorMessage ? (
          <div className="text-sm text-rose-600">{errorMessage}</div>
        ) : null}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className="relative"
          >
            <span className={isLoading ? 'invisible' : ''}>Create</span>
            {isLoading ? (
              <span className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin" />
              </span>
            ) : null}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
